/* eslint-disable sonarjs/no-duplicate-string */
import { getFeatureSwitches } from "../api/featureSwitches";
import type { AppMetadata } from "../appBridge/AppMetadata";
import { StudentManager } from "../controllers/StudentManager";
import { TeacherManager } from "../controllers/TeacherManager";
import { UserManager } from "../controllers/UserManager";
import { Logger } from "../Logging/Log";
import { currentEnvIsDevOrLocal } from "../utils/env";
import localStorage from "../utils/localStorage";
import { FEATURE_SWITCHES, FEATURE_SWITCHES_TO_CHECK } from "./constants";
import { evaluateFeatureSwitchCondition, type EvaluationConfig } from "./evaluateFeatureSwitchCondition";
import { FeatureSwitchQueryBuilder } from "./FeatureSwitchQuery";
import type { FeatureSwitchesResponse } from "./IFeatureSwitchManager";

const FEATURE_SET_ID_LOCALSTORAGE_KEY = "monsterWorld/featureSwitches/featureSetId";

const OFF_VARIANT = "off";

const FS_ENABLED_VARIANTS: string[] = ["on", "test", "beta", "rollout"];

type JsonValue = string | number | boolean | undefined | JsonValue[] | { [key: string]: JsonValue };

export const EVALUATION_CONFIG: EvaluationConfig = {
  isFeatureSwitchEnabled: (fsName: string) => FeatureSwitchManager.isFeatureSwitchEnabled(fsName),
  isFeatureSwitchVariantEnabled: (fsName: string, variant?: string) => {
    const foundVariant = FeatureSwitchManager.getFeatureSwitchVariant(fsName);
    if (!foundVariant || !variant) {
      return false;
    }

    return foundVariant === variant;
  },
};

export type FeatureSwitchChecker = (fsName: string, variant: string) => boolean;
export class FeatureSwitchManager {
  static featureSwitches: Record<string, string> | undefined = undefined;
  static customConfig?: unknown;
  private static enabledFeatureSwitches: string[] | undefined;
  static appMetadata: AppMetadata = {};
  static islandCreationDate?: Date | null = undefined;

  static reset() {
    FeatureSwitchManager.featureSwitches = undefined;
    FeatureSwitchManager.customConfig = undefined;
    FeatureSwitchManager.enabledFeatureSwitches = undefined;
  }

  static setFeatureSwitches(switches: Record<string, string>) {
    FeatureSwitchManager.featureSwitches = switches;
  }

  /** Note: This fetch method is only used by customizer and local builds at the moment.
   * The Launcher uses feature switches from the ticket response.
   */
  static async fetchFeatureSwitches(classIslandId?: string, communityId?: string) {
    const user = UserManager.instance.user;
    if (!user) {
      console.error("Error fetching feature switches when not signed in.");
      return;
    }

    const entityId = UserManager.instance.getEntityId() || "";

    const schoolId = (user.type === "teacher" ? user.teacher.schoolId : user.student.schoolIds?.[0]) || "";
    const classes = user.type === "teacher" ? TeacherManager.instance.classes : StudentManager.instance.classes;
    const classroomIds = classes.flatMap((c) => c._id);

    // get local value for featureSetId to be used on API request caching management
    const featureSetIdKey = `monsterverse/${FEATURE_SET_ID_LOCALSTORAGE_KEY}/${entityId}`;
    const featureSetId = localStorage.getItem(featureSetIdKey) || "";
    const locale = window.navigator?.language || "en-US"; // fallback to en-US if no locale is set.

    const query = FeatureSwitchQueryBuilder.buildQuery(
      entityId,
      schoolId,
      communityId ?? "",
      classroomIds,
      FEATURE_SWITCHES_TO_CHECK.userSwitches,
      FEATURE_SWITCHES_TO_CHECK.schoolSwitches,
      FEATURE_SWITCHES_TO_CHECK.classroomSwitches,
      FEATURE_SWITCHES_TO_CHECK.communitySwitches,
      FEATURE_SWITCHES_TO_CHECK.classOwnerSwitches,
      undefined, // TODO: figure out how to get classOwnerId if needed (currently customizer doesn't fetch classes)
      featureSetId,
      locale
    );
    const data = await getFeatureSwitches(query, classIslandId);
    // update local value for featureSetId to be used on future requests
    data.featureSetId && localStorage.setItem(featureSetIdKey, data.featureSetId);

    FeatureSwitchManager.featureSwitches = FeatureSwitchManager.extractFeatureSwitches(data, classIslandId, entityId);
    FeatureSwitchManager.customConfig = data.customConfig;
  }

  static extractFeatureSwitches(
    response: FeatureSwitchesResponse,
    selectedClass?: string,
    entityId?: string
  ): Record<string, string> {
    let classroomFSs: Record<string, string> = {};
    if (selectedClass) {
      // Only use classroom FS if a selectedClass is provided! Note, we no longer fallback
      // on the most permissive class setting if class is not present.
      const classroomKey = selectedClass as `class-${string}`;
      classroomFSs = response[classroomKey];

      if (classroomFSs === undefined) {
        const classroomKeys = Object.keys(response).filter((k) => /class-.*/g.test(k));
        // If there are any classroom keys, then we should have found a match for the selected class
        if (classroomKeys.length > 0) {
          console.log(`[info] No feature switches found for user (${entityId}) in class (${selectedClass})`);
        }
      }
    }

    // merge feature switch variants across all "types" of FS, taking most permissive value
    const allSwitches = [
      response.user ?? {},
      response.school ?? {},
      classroomFSs ?? {},
      response.community ?? {},
      response.classOwner ?? {},
    ];

    return allSwitches.reduce((acc, cur) => {
      Object.entries(cur).forEach(([key, value]) => {
        // discard Off in favor of an actual variant group. IMPORTANT to note that 'control' is a
        // variant group and needs to be logged. If there is
        // a conflict between control & an 'enabled' group, take the enabled group.
        // NOTE: this "permissiveness" will totally screw up experiment populations, the solution
        // is to NOT check diff entity types against the same FS.
        if (!acc[key] || acc[key] === OFF_VARIANT || FS_ENABLED_VARIANTS.includes(value)) {
          acc[key] = value;
        }
      });
      return acc;
    });
  }

  /**
   * You should generally use isFeatureSwitchEnabled UNLESS you are
   * checking for a specific case that is not `test`, `on`, or `beta`.
   */
  static isFeatureSwitchVariant(fsName: string, variant: string): boolean {
    const isEditor = window?.EDITOR?.EDIT_MODE === true;
    if (FeatureSwitchManager.featureSwitches == undefined) {
      if (currentEnvIsDevOrLocal() && !isEditor) {
        throw new Error("isFeatureSwitchVariant: Feature switches not loaded");
      } else {
        !isEditor && Logger.logError("isFeatureSwitchVariant: Feature switches not loaded");
        return false;
      }
    }

    const isInVariant = FeatureSwitchManager.featureSwitches[fsName] === variant;
    return isInVariant && FeatureSwitchManager.isValidFeatureSwitchForCurrentPlatform(fsName);
  }

  /**
   * You should generally use isFeatureSwitchEnabled UNLESS you are
   * checking for a specific case that is not `test`, `on`, or `beta`.
   */
  static getFeatureSwitchVariant(fsName: string): string | undefined {
    return FeatureSwitchManager.featureSwitches ? FeatureSwitchManager.featureSwitches[fsName] : undefined;
  }

  static isFeatureSwitchEnabled(fsName: string): boolean {
    if (FeatureSwitchManager.featureSwitches == undefined) {
      const isEditor = window?.EDITOR?.EDIT_MODE === true;
      if (currentEnvIsDevOrLocal() && !isEditor) {
        throw new Error(`isFeatureSwitchEnabled: Feature switches not loaded when checking on ${fsName}`);
      } else {
        !isEditor && Logger.logError(`isFeatureSwitchEnabled: Feature switches not loaded when checking on ${fsName}`);
        return false;
      }
    }
    const enabledSwitches = FeatureSwitchManager.getEnabledFeatureSwitches();
    return enabledSwitches.includes(fsName);
  }

  static isFeatureSwitchEnabledWithCreationDate(fsName: string, date: Date): boolean {
    return !!(
      FeatureSwitchManager.islandCreationDate &&
      FeatureSwitchManager.islandCreationDate > date &&
      this.isFeatureSwitchEnabled(fsName)
    );
  }

  // Gets a flat list of the enabled feature switches based on the "variants" passed in
  static getEnabledFeatureSwitches(variants: string[] = FS_ENABLED_VARIANTS) {
    const isEditor = window?.EDITOR?.EDIT_MODE === true;
    if (FeatureSwitchManager.featureSwitches == undefined) {
      if (currentEnvIsDevOrLocal() && !isEditor) {
        throw new Error("getEnabledFeatureSwitches: Feature switches not loaded");
      } else {
        !isEditor && Logger.logError("getEnabledFeatureSwitches: Feature switches not loaded");
        return [];
      }
    }

    if (FeatureSwitchManager.enabledFeatureSwitches == undefined) {
      FeatureSwitchManager.enabledFeatureSwitches = Object.entries(FeatureSwitchManager.featureSwitches)
        .filter((x) => variants.includes(x[1]))
        .map((x) => x[0]);
      console.log(
        "Setting enabled feature switches",
        FeatureSwitchManager.enabledFeatureSwitches,
        FeatureSwitchManager.customConfig
      );
    }
    return FeatureSwitchManager.enabledFeatureSwitches;
  }

  static isValidFeatureSwitchForCurrentPlatform(fsName: string): boolean {
    const platform = FeatureSwitchManager.appMetadata.platform?.toLowerCase() || "web";
    const name = fsName.toLowerCase();
    const fsPrefixesByPlatorm: Record<string, string> = {
      ios: "ios_",
      android: "android_",
      android_web: "android_",
      web: "web",
    };
    const platformSpecificFSPrefixes = Object.values(fsPrefixesByPlatorm);
    const isFeatureSwitchPlatormSpecific = platformSpecificFSPrefixes.some((prefix) => name.startsWith(prefix));
    return !isFeatureSwitchPlatormSpecific || name.startsWith(fsPrefixesByPlatorm[platform]);
  }
}

export function entityDeserializeFSValidator(json: Record<string, JsonValue>, performanceTier?: number) {
  //Check for feature switches disabling the entity
  if (
    "Feature Switch" in json &&
    json["Feature Switch"] &&
    typeof json["Feature Switch"] === "object" &&
    "name" in json["Feature Switch"]
  ) {
    const fsNames = json["Feature Switch"]["name"];

    // Deprecated: "name" field as a `string` value.
    if (typeof fsNames === "string") {
      if (!evaluateFeatureSwitchCondition(fsNames, EVALUATION_CONFIG)) {
        return false;
      }

      // Modern-path: "name" field as an array of `string` values.
    } else if (Array.isArray(fsNames) && fsNames.length > 0) {
      const isEnabled = fsNames.some((name) => {
        if (typeof name === "string") {
          return evaluateFeatureSwitchCondition(name, EVALUATION_CONFIG);
        }

        // This should never happen.
        throw new Error("Unexpected type in feature switch array.");
      });

      if (!isEnabled) {
        return false;
      }
    }
  }

  //If performance tier is passed in, check for it being too low for the entity
  // eslint-disable-next-line sonarjs/prefer-single-boolean-return
  if (
    performanceTier !== undefined &&
    "Performance Tier" in json &&
    json["Performance Tier"] &&
    typeof json["Performance Tier"] === "object" &&
    "requiredPerformanceTier" in json["Performance Tier"] &&
    typeof json["Performance Tier"]["requiredPerformanceTier"] === "number" &&
    json["Performance Tier"]["requiredPerformanceTier"] > performanceTier
  ) {
    return false;
  }

  if (json.decal) {
    return (
      FeatureSwitchManager.isFeatureSwitchEnabled(FEATURE_SWITCHES.MONSTERVERSE_USE_DECALS) &&
      (window.performanceOptions?.useDepthEffects ?? false)
    );
  }

  //Load the entity
  return true;
}
